import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Wrench } from 'lucide-react';
import { SystemStatusMode } from '../models';
import { getApiError, updateSystemMaintenance } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Modal } from './ui/modal';
import { Select } from './ui/select';

const modeLabels: Record<SystemStatusMode, string> = {
  normal: 'Нормальная работа',
  maintenance: 'Технические работы',
  incident: 'Сбой системы',
};

const reasonOptions: Record<SystemStatusMode, Array<{ value: string; label: string }>> = {
  maintenance: [
    { value: 'planned_update', label: 'Плановое обновление' },
    { value: 'deployment', label: 'Выкладка новой версии' },
    { value: 'infrastructure', label: 'Работы с инфраструктурой' },
    { value: 'other', label: 'Другая причина' },
  ],
  incident: [
    { value: 'api_outage', label: 'Недоступность API' },
    { value: 'integration_failure', label: 'Сбой интеграций' },
    { value: 'terminal_failure', label: 'Массовая проблема терминалов' },
    { value: 'data_issue', label: 'Проблема с данными' },
    { value: 'other', label: 'Другая причина' },
  ],
  normal: [
    { value: 'resolved', label: 'Работы завершены / сбой устранён' },
    { value: 'cancelled', label: 'Режим был включён ошибочно' },
    { value: 'other', label: 'Другая причина' },
  ],
};

interface Props {
  open: boolean;
  currentMode: SystemStatusMode;
  initialMode: SystemStatusMode;
  onClose: () => void;
  onChanged: (mode: SystemStatusMode) => void;
}

export function SystemStatusChangeModal({ open, currentMode, initialMode, onClose, onChanged }: Props) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState<SystemStatusMode>(initialMode);
  const [reasonCategory, setReasonCategory] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setMode(initialMode);
    setReasonCategory('');
    setReason('');
    setPassword('');
    setConfirmed(false);
    setError('');
  }, [open, initialMode]);

  const categoryLabel = useMemo(
    () => reasonOptions[mode].find((item) => item.value === reasonCategory)?.label || '—',
    [mode, reasonCategory],
  );

  const chooseMode = (value: SystemStatusMode) => {
    setMode(value);
    setReasonCategory('');
  };

  const next = () => {
    setError('');
    if (step === 1 && mode === currentMode) return setError('Выберите режим, отличающийся от текущего');
    if (step === 2 && !reasonCategory) return setError('Выберите категорию причины');
    if (step === 2 && reason.trim().length < 10) return setError('Опишите причину минимум 10 символами');
    setStep((value) => Math.min(3, value + 1));
  };

  const submit = async () => {
    if (!confirmed) return setError('Подтвердите, что понимаете последствия');
    if (!password) return setError('Введите пароль подтверждения');
    setSaving(true);
    setError('');
    try {
      const response = await updateSystemMaintenance({
        mode,
        reasonCategory,
        reason: reason.trim(),
        confirmationPassword: password,
      });
      onChanged(response.data.data.mode);
      onClose();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => { if (!saving) onClose(); }}
      title={`Изменение статуса системы — шаг ${step} из 3`}
      footer={(
        <>
          <Button variant="outline" onClick={step === 1 ? onClose : () => setStep((value) => value - 1)} disabled={saving}>
            {step === 1 ? 'Отмена' : 'Назад'}
          </Button>
          {step < 3
            ? <Button onClick={next}>Далее</Button>
            : <Button variant={mode === 'incident' ? 'destructive' : 'default'} onClick={submit} disabled={saving}>{saving ? 'Сохранение…' : 'Подтвердить изменение'}</Button>}
        </>
      )}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          {[1, 2, 3].map((value) => <div key={value} className={`h-1.5 flex-1 rounded-full ${value <= step ? 'bg-primary' : 'bg-muted'}`} />)}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Выберите единственный глобальный режим. Текущий: <strong>{modeLabels[currentMode]}</strong>.</p>
            {(['normal', 'maintenance', 'incident'] as SystemStatusMode[]).map((value) => (
              <button key={value} type="button" onClick={() => chooseMode(value)} className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-colors ${mode === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}>
                {value === 'normal' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : value === 'maintenance' ? <Wrench className="h-5 w-5 text-warning" /> : <AlertTriangle className="h-5 w-5 text-destructive" />}
                <div><div className="font-semibold">{modeLabels[value]}</div><div className="text-xs text-muted-foreground">{value === currentMode ? 'Текущий режим' : value === 'normal' ? 'Убрать все заглушки' : 'Показать заглушку всем клиентам и терминалам'}</div></div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg bg-muted/60 px-4 py-3 text-sm">Новый режим: <strong>{modeLabels[mode]}</strong></div>
            <Select label="Категория причины" value={reasonCategory} onChange={(event) => setReasonCategory(event.target.value)}>
              <option value="">Выберите причину</option>
              {reasonOptions[mode].map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </Select>
            <div className="space-y-1.5">
              <label htmlFor="system-status-reason" className="text-sm font-medium">Подробное описание</label>
              <textarea id="system-status-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} rows={5} placeholder="Что произошло, какие работы ведутся или что было исправлено" className="w-full resize-none rounded-lg border border-input bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              <p className="text-right text-xs text-muted-foreground">{reason.length}/1000</p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-sm">
              <div><span className="text-muted-foreground">Режим:</span> <strong>{modeLabels[mode]}</strong></div>
              <div><span className="text-muted-foreground">Категория:</span> {categoryLabel}</div>
              <div><span className="text-muted-foreground">Причина:</span> {reason}</div>
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm">
              <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} className="mt-0.5 h-4 w-4" />
              <span>Я понимаю, что изменение будет применено ко всем личным кабинетам и терминалам.</span>
            </label>
            <Input label="Отдельный пароль подтверждения" type="password" autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} />
          </div>
        )}

        {error && <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      </div>
    </Modal>
  );
}
